import React from 'react'
import { OfficeCitiesMappingPropsEntity, PlaceSelectType, RegionDetailsEntity } from '../../../interface/offices'
import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useMap } from '@vis.gl/react-google-maps';
import { toast } from 'react-toastify';
import { toastOptObj } from '../../../utils/constants';
import { Dropdown } from 'primereact/dropdown';
import { AutoComplete } from 'primereact/autocomplete';
import { IsNullOrUndefined, IsStringNullEmptyOrUndefined } from '../../../utils/null-check';

const MapFeatureLayer = ({ currentOffice,
    assignments,
    initialSelectedCities = [],
    onSelectedAreasChange
}: OfficeCitiesMappingPropsEntity) => {
    const map = useMap();
    const [selectedUnassignedCities, setSelectedUnassignedCities] = useState<string[]>([]);
    const [pendingPlaceId, setPendingPlaceId] = useState<string | null>(null);
    const [searchValue, setSearchValue] = useState<string>('');
    const searchBoxRef = useRef<HTMLInputElement>(null);
    const listenerRef = useRef<google.maps.MapsEventListener | null>(null);
    const [selectedAreas, setSelectedAreas] = useState<RegionDetailsEntity[]>(initialSelectedCities);
    const [localAssignments, setLocalAssignments] = useState<RegionDetailsEntity[]>(assignments);
    const searchControlRef = useRef<HTMLDivElement>(null);
    const legendControlRef = useRef<HTMLDivElement>(null);
    const [selectedPlaceType, setSelectedPlaceType] = useState<PlaceSelectType>('LOCALITY');
    const [suggestions, setSuggestions] = useState<any[]>([]);

    const [selectedOffices, setSelectedOffices] = useState<string[]>(() =>
        assignments
            .filter(a => a.officeId && a.officeId !== currentOffice.id)
            .map(a => a.officeId)
            .filter((v, i, arr) => arr.indexOf(v) === i)
    );

    const [isCurrentOfficeSelected, setIsCurrentOfficeSelected] = useState<boolean>(true);

    useEffect(() => {
        const otherOfficeIds = localAssignments
            .filter(a => a.officeId && a.officeId !== currentOffice.id)
            .map(a => a.officeId)
            .filter((v, i, arr) => arr.indexOf(v) === i);

        setSelectedOffices(prev => {
            const newIds = otherOfficeIds.filter(id => !prev.includes(id));
            return newIds.length > 0 ? [...prev, ...newIds] : prev;
        });
    }, [localAssignments, currentOffice.id]);

    const toggleOffice = (officeId: string) => {
        setSelectedOffices(prev => {
            if (prev.includes(officeId)) {
                return prev.filter(id => id !== officeId);
            } else {
                return [...prev, officeId];
            }
        });
    };


    useEffect(() => {
        if (!map || !searchControlRef.current || !legendControlRef.current) return;

        map.controls[google.maps.ControlPosition.TOP_LEFT].push(
            searchControlRef.current
        );

        map.controls[google.maps.ControlPosition.LEFT_BOTTOM].push(
            legendControlRef.current
        );

        return () => {
            map.controls[google.maps.ControlPosition.TOP_RIGHT].clear();
            map.controls[google.maps.ControlPosition.LEFT_BOTTOM].clear();
        };
    }, [map]);


    useEffect(() => {
        setSelectedAreas(initialSelectedCities ?? []);
    }, [initialSelectedCities]);

    useEffect(() => {
        const currentOfficeAssignments = selectedAreas.map(area => ({
            ...area,
            officeId: currentOffice.id
        }));
        const otherOfficesAssignments = assignments.filter(a => a.officeId !== currentOffice.id);
        setLocalAssignments([...otherOfficesAssignments, ...currentOfficeAssignments]);
    }, [assignments, selectedAreas, currentOffice.id]);

    useEffect(() => {
        onSelectedAreasChange?.(selectedAreas);
    }, [selectedAreas, onSelectedAreasChange]);

    const blockedDivisionIds = useMemo(
        () =>
            new Set(
                localAssignments
                    .filter(a => a.city_providerId)
                    .map(a => a.division_providerId)
            ),
        [localAssignments]
    );

    const blockedStateIds = useMemo(
        () =>
            new Set(
                localAssignments
                    .filter(a => a.city_providerId || a.division_providerId)
                    .map(a => a.state_providerId)
            ),
        [localAssignments]
    );

    const blockedCountryIds = useMemo(
        () =>
            new Set(
                localAssignments
                    .filter(a => a.city_providerId || a.division_providerId || a.state_providerId)
                    .map(a => a.country_providerId)
            ),
        [localAssignments]
    );

    const GOLDEN_ANGLE = 137.508;

    const LIGHTNESS_LEVELS = [38, 48, 58, 68];

    const generateOfficeColor = (officeId: string) => {
        let hash = 0;
        for (let i = 0; i < officeId.length; i++) {
            hash = (hash * 31 + officeId.charCodeAt(i)) >>> 0;
        }

        // Evenly spread hues
        let hue = (hash * GOLDEN_ANGLE) % 360;
        if (hue >= 10 && hue <= 50) {
            hue = (hue + 120) % 360;
        }
        // Pick clearly different lightness (not tiny delta)
        const lightness = LIGHTNESS_LEVELS[hash % LIGHTNESS_LEVELS.length];

        // Slight saturation variation but still strong
        const saturation = 75 + (hash % 15); // 75–90

        return {
            fill: `hsl(${hue}, ${saturation}%, ${lightness}%)`,
            stroke: `hsl(${hue}, ${saturation}%, ${Math.max(lightness - 25, 15)}%)`
        };
    };


    const officesForLegend = useMemo(() => {
        const map = new Map<
            string,
            { officeId: string; officeName: string }
        >();

        localAssignments.forEach(a => {
            if (a.officeId && !map.has(a.officeId)) {
                map.set(a.officeId, {
                    officeId: a.officeId,
                    officeName: a.officeName ?? a.officeId
                });
            }
        });

        return Array.from(map.values());
    }, [localAssignments]);

    const placeTypeOptions = useMemo(() => {
        return [
            { label: 'City', value: 'LOCALITY' },
            { label: 'Division', value: 'ADMINISTRATIVE_AREA_LEVEL_2' },
            { label: 'State', value: 'ADMINISTRATIVE_AREA_LEVEL_1' },
            { label: 'Country', value: 'COUNTRY' }
        ];
    }, []);

    useEffect(() => {
        if (initialSelectedCities.length > 0) {
            const placeIds: string[] = [];
            initialSelectedCities.forEach((place) => {
                if (place.type === 'COUNTRY' && place.country_providerId) {
                    placeIds.push(place.country_providerId);
                } else if (place.type === 'ADMINISTRATIVE_AREA_LEVEL_1' && place.state_providerId) {
                    placeIds.push(place.state_providerId);
                } else if (place.type === 'ADMINISTRATIVE_AREA_LEVEL_2' && place.division_providerId) {
                    placeIds.push(place.division_providerId);
                } else if (place.city_providerId) {
                    placeIds.push(place.city_providerId);
                }
            });
            setSelectedUnassignedCities(placeIds);
        } else {
            setSelectedUnassignedCities([]);
        }
    }, [initialSelectedCities]);

    const getPlaceDetails = useCallback(async (placeId: string) => {
        if (!map) return;

        const service = new google.maps.places.PlacesService(map);
        service.getDetails(
            { placeId: placeId, fields: ['name', 'formatted_address', 'geometry'] },
            (place, status) => {
                setPendingPlaceId(null);
            }
        );
    }, [map]);

    useEffect(() => {
        if (pendingPlaceId) {
            getPlaceDetails(pendingPlaceId);
        }
    }, [pendingPlaceId, getPlaceDetails]);

    const assignmentMaps = useMemo(() => {
        const city = new Map<string, RegionDetailsEntity>();
        const division = new Map<string, RegionDetailsEntity>();
        const state = new Map<string, RegionDetailsEntity>();
        const country = new Map<string, RegionDetailsEntity>();

        localAssignments.forEach(a => {
            if (a.city_providerId) city.set(a.city_providerId, a);
            else if (a.division_providerId) division.set(a.division_providerId, a);
            else if (a.state_providerId) state.set(a.state_providerId, a);
            else if (a.country_providerId) country.set(a.country_providerId, a);
        });

        return { city, division, state, country };
    }, [localAssignments]);


    useEffect(() => {
        if (!map) return;

        // Apply styling to all feature types
        const featureTypes = ["LOCALITY", "ADMINISTRATIVE_AREA_LEVEL_1", 'ADMINISTRATIVE_AREA_LEVEL_2', "COUNTRY"];

        featureTypes.forEach((featureType) => {
            const featureLayer = map.getFeatureLayer(featureType as google.maps.FeatureType);

            featureLayer.style = (feature) => {
                const placeId = (feature.feature as any)?.placeId;
                // const assignment = (() => {
                //     if (!placeId) return undefined;

                //     if (featureType === 'LOCALITY') {
                //         return localAssignments.find(a => a.city_providerId === placeId);
                //     }

                //     if (featureType === 'ADMINISTRATIVE_AREA_LEVEL_2') {
                //         return localAssignments.find(
                //             a =>
                //                 a.division_providerId === placeId &&
                //                 !a.city_providerId
                //         );
                //     }

                //     if (featureType === 'ADMINISTRATIVE_AREA_LEVEL_1') {
                //         return localAssignments.find(
                //             a =>
                //                 a.state_providerId === placeId &&
                //                 !a.division_providerId &&
                //                 !a.city_providerId &&
                //                 !(a as any).cityId
                //         );
                //     }

                //     if (featureType === 'COUNTRY') {
                //         return localAssignments.find(
                //             a =>
                //                 a.country_providerId === placeId &&
                //                 !a.division_providerId &&
                //                 !a.state_providerId &&
                //                 !a.city_providerId
                //         );
                //     }

                //     return undefined;
                // })();
                let assignment;
                if (featureType === 'LOCALITY') {
                    assignment = assignmentMaps.city.get(placeId);
                } else if (featureType === 'ADMINISTRATIVE_AREA_LEVEL_2') {
                    assignment = assignmentMaps.division.get(placeId);
                } else if (featureType === 'ADMINISTRATIVE_AREA_LEVEL_1') {
                    assignment = assignmentMaps.state.get(placeId);
                } else if (featureType === 'COUNTRY') {
                    assignment = assignmentMaps.country.get(placeId);
                }


                // Highlight selected features (search results or clicked cities) - orange
                if (placeId && selectedUnassignedCities.includes(placeId)) {
                    if (isCurrentOfficeSelected) {
                        return {
                            fillColor: '#E65100',
                            fillOpacity: 0.7,
                            strokeColor: '#BF360C',
                            strokeWeight: 3
                        };
                    }
                }

                // Office-assigned cities and administrative areas
                if (assignment) {
                    const officeColor = generateOfficeColor(assignment.officeId);

                    // Only show office colors for OTHER offices.
                    // If it's assigned to the current office and the user unselects it,
                    // it should go back to the default styling (not stay colored).
                    if (assignment.officeId !== currentOffice.id) {
                        if (selectedOffices.includes(assignment.officeId)) {
                            return {
                                fillColor: officeColor.fill,
                                fillOpacity: 0.6,
                                strokeColor: officeColor.stroke,
                                strokeWeight: 2
                            };
                        }
                    }
                }

                const defaultStroke = getDefaultStrokeByType(featureType as PlaceSelectType);

                if (featureType !== selectedPlaceType) {
                    return {
                        fillColor: '#E0E0E0',
                        fillOpacity: 0.01,
                        strokeOpacity: 0,
                        strokeWeight: 0
                    };
                }

                // Default styling 
                return {
                    fillColor: '#63b5f8',
                    fillOpacity: 0.05,
                    strokeColor: defaultStroke?.color ?? '#91cbfa',
                    strokeOpacity: defaultStroke?.opacity ?? 0.2,
                    strokeWeight: defaultStroke?.weight ?? 1
                };
            };

            // Only add click listener once
            if (featureType === selectedPlaceType) {
                if (listenerRef.current) {
                    google.maps.event.removeListener(listenerRef.current);
                }
                listenerRef.current = featureLayer.addListener(
                    'click',
                    (event: google.maps.FeatureMouseEvent) => {
                        const placeId = (event.features?.[0] as google.maps.Feature & { placeId?: string })?.placeId;
                        if (!placeId) return;

                        console.log('[MapFeatureLayer] city/feature clicked -> providerId (placeId):', placeId, '| selectedPlaceType:', selectedPlaceType);

                        const service = new google.maps.places.PlacesService(map);
                        service.getDetails(
                            { placeId: placeId, fields: ['geometry', 'name', 'type', 'formatted_address'] },
                            (place, status) => {
                                if (status === google.maps.places.PlacesServiceStatus.OK && place?.geometry?.location) {
                                    const geocoder = new google.maps.Geocoder();
                                    geocoder.geocode({ location: place.geometry.location }, (results, geoStatus) => {
                                        if (geoStatus === google.maps.GeocoderStatus.OK && results) {
                                            const adminArea = results.find(result =>
                                                result.types.includes('administrative_area_level_1')
                                            );

                                            const divisionArea = results.find(result =>
                                                result.types.includes('administrative_area_level_2')
                                            );

                                            const country = results.find(result =>
                                                result.types.includes('country')
                                            );

                                            // IMPORTANT: the placeId clicked on the map (from the FeatureLayer event) is the
                                            // exact, authoritative id for whichever level is currently selected.
                                            // Re-deriving it via reverse-geocoding the centroid (as done below for the
                                            // ancestor levels) can resolve to a DIFFERENT neighboring feature when the
                                            // FeatureLayer boundary dataset and the Geocoding API dataset disagree near
                                            // borders - this was causing the wrong division/state/country to be saved.
                                            let statePlaceId: string | null =
                                                selectedPlaceType === 'ADMINISTRATIVE_AREA_LEVEL_1' ? placeId : null;
                                            if (!statePlaceId && adminArea && (adminArea as any).place_id) {
                                                statePlaceId = (adminArea as any).place_id;
                                            }

                                            let divisionPlaceId: string | null =
                                                selectedPlaceType === 'ADMINISTRATIVE_AREA_LEVEL_2' ? placeId : null;
                                            if (!divisionPlaceId && divisionArea && (divisionArea as any).place_id) {
                                                divisionPlaceId = (divisionArea as any).place_id;
                                            }

                                            let countryPlaceId: string | null =
                                                selectedPlaceType === 'COUNTRY' ? placeId : null;
                                            if (!countryPlaceId && country && (country as any).place_id) {
                                                countryPlaceId = (country as any).place_id;
                                            }

                                            const isInvalidHierarchy =
                                                (selectedPlaceType === 'LOCALITY' && (!placeId || !statePlaceId || !countryPlaceId)) ||

                                                (selectedPlaceType === 'ADMINISTRATIVE_AREA_LEVEL_2' &&
                                                    (!divisionPlaceId || !statePlaceId || !countryPlaceId ||
                                                        divisionPlaceId === statePlaceId)) ||

                                                (selectedPlaceType === 'ADMINISTRATIVE_AREA_LEVEL_1' &&
                                                    (!statePlaceId || !countryPlaceId ||
                                                        statePlaceId === countryPlaceId)) ||

                                                (selectedPlaceType === 'COUNTRY' && !countryPlaceId);

                                            if (isInvalidHierarchy) {
                                                console.log("BLOCKED CLICK:", {
                                                    placeId,
                                                    statePlaceId,
                                                    divisionPlaceId,
                                                    countryPlaceId
                                                });

                                                toast.warning('Mapping skipped due to incomplete or restricted location data', {
                                                    ...toastOptObj
                                                });

                                                return;
                                            }

                                            // 1. Check if city is already assigned
                                            const existing = localAssignments.find(a => a.city_providerId === placeId);
                                            if (existing && existing.officeId !== currentOffice.id) {
                                                toast.warning('This city is already assigned to another office', { ...toastOptObj });
                                                return;
                                            }

                                            // 2. Check if division is already assigned
                                            const existingDivision = localAssignments.find(a => a.division_providerId === divisionPlaceId && !a.city_providerId && !a.cityId);
                                            if (existingDivision && existingDivision.officeId !== currentOffice.id) {
                                                toast.warning('This division or its cities are already assigned to another office', { ...toastOptObj });
                                                return;
                                            }

                                            if (existingDivision && existingDivision.officeId === currentOffice.id && selectedPlaceType !== 'ADMINISTRATIVE_AREA_LEVEL_2') {
                                                toast.warning('This division or its cities are already assigned to the current office', { ...toastOptObj });
                                                return;
                                            }

                                            // 3. Check if parent state is already assigned
                                            const assignState = localAssignments.find(
                                                a => a.state_providerId === statePlaceId && !a.division_providerId && !a.city_providerId && !a.cityId
                                            );

                                            if (assignState && assignState.officeId !== currentOffice.id && selectedPlaceType !== 'COUNTRY') {
                                                toast.warning('This state or cities/divisions of this state is already assigned to another office', { ...toastOptObj });
                                                return;
                                            }

                                            if (assignState && assignState.officeId === currentOffice.id && selectedPlaceType !== 'ADMINISTRATIVE_AREA_LEVEL_1') {
                                                toast.warning('This state or cities/divisions of this state is already assigned to the current office', { ...toastOptObj });
                                                return;
                                            }


                                            // 4. Check if parent country is already assigned
                                            const assignCountry = localAssignments.find(
                                                a => a.country_providerId === countryPlaceId && !a.division_providerId && !a.state_providerId && !a.city_providerId
                                            );
                                            if (assignCountry && assignCountry.officeId !== currentOffice.id) {
                                                toast.warning('This country or its states/divisions/cities are already assigned to another office', { ...toastOptObj });
                                                return;
                                            }

                                            if (assignCountry && assignCountry.officeId === currentOffice.id && selectedPlaceType !== 'COUNTRY') {
                                                toast.warning('This country or its states/divisions/cities are already assigned to the current office', { ...toastOptObj });
                                                return;
                                            }

                                            if (
                                                place.types?.includes('country') &&
                                                placeId &&
                                                blockedCountryIds.has(placeId)
                                            ) {
                                                toast.warning(
                                                    'Please remove mapped states/divisions/cities before assigning a country.',
                                                    { ...toastOptObj }
                                                );
                                                return;
                                            }

                                            if (
                                                place.types?.includes('administrative_area_level_1') &&
                                                placeId &&
                                                blockedStateIds.has(placeId)
                                            ) {
                                                toast.warning(
                                                    'Please remove mapped cities/divisions before assigning a state.',
                                                    { ...toastOptObj }
                                                );
                                                return;
                                            }

                                            if (
                                                place.types?.includes('administrative_area_level_2') &&
                                                placeId &&
                                                blockedDivisionIds.has(placeId)
                                            ) {
                                                toast.warning(
                                                    'Please remove mapped cities before assigning a division.',
                                                    { ...toastOptObj }
                                                );
                                                return;
                                            }

                                            setSelectedUnassignedCities(prev => {
                                                const isSelected = prev.includes(placeId);

                                                setSelectedAreas(prevAreas => {
                                                    if (isSelected) {
                                                        return prevAreas.filter(
                                                            area => !isSameAreaByType(area, placeId, selectedPlaceType)
                                                        );
                                                    }

                                                    const nextArea: RegionDetailsEntity = {
                                                        officeId: currentOffice.id,
                                                        type: selectedPlaceType,

                                                        country_providerId:
                                                            selectedPlaceType === 'COUNTRY' ||
                                                                selectedPlaceType === 'ADMINISTRATIVE_AREA_LEVEL_1' ||
                                                                selectedPlaceType === 'ADMINISTRATIVE_AREA_LEVEL_2' ||
                                                                selectedPlaceType === 'LOCALITY'
                                                                ? (countryPlaceId ?? undefined)
                                                                : undefined,

                                                        state_providerId:
                                                            selectedPlaceType === 'ADMINISTRATIVE_AREA_LEVEL_1' ||
                                                                selectedPlaceType === 'ADMINISTRATIVE_AREA_LEVEL_2' ||
                                                                selectedPlaceType === 'LOCALITY'
                                                                ? (statePlaceId ?? undefined)
                                                                : undefined,

                                                        division_providerId:
                                                            selectedPlaceType === 'ADMINISTRATIVE_AREA_LEVEL_2' ||
                                                                selectedPlaceType === 'LOCALITY'
                                                                ? (divisionPlaceId ?? undefined)
                                                                : undefined,

                                                        city_providerId:
                                                            selectedPlaceType === 'LOCALITY'
                                                                ? placeId
                                                                : undefined,

                                                        placeName: selectedPlaceType === 'LOCALITY' && place && place.name
                                                            ? (() => {
                                                                const getAddressComponent = (type: string) => {
                                                                    for (const res of results || []) {
                                                                        const comp = res.address_components ? res.address_components.find((c: any) => c.types && c.types.includes(type)) : null;
                                                                        if (comp) return comp.long_name;
                                                                    }
                                                                    return '';
                                                                };
                                                                const cityName = place.name;
                                                                const districtName = getAddressComponent('administrative_area_level_3') || getAddressComponent('administrative_area_level_2');
                                                                const stateName = getAddressComponent('administrative_area_level_1');
                                                                const countryName = getAddressComponent('country');
                                                                return districtName
                                                                    ? `${cityName}, ${districtName}, ${stateName}, ${countryName}`
                                                                    : `${cityName}, ${stateName}, ${countryName}`;
                                                            })()
                                                            : (place ? place.formatted_address : '')
                                                    };

                                                    if (
                                                        prevAreas.some(area =>
                                                            isSameAreaByType(area, placeId, selectedPlaceType)
                                                        )
                                                    ) {
                                                        return prevAreas;
                                                    }

                                                    return [...prevAreas, nextArea];
                                                });

                                                if (isSelected) {
                                                    return prev.filter(id => id !== placeId);
                                                }

                                                setPendingPlaceId(placeId);
                                                return [...prev, placeId];
                                            });

                                        }
                                    });
                                }
                            }
                        );
                    }
                );
            }
        });

        return () => {

        };
    }, [map, localAssignments, currentOffice, selectedUnassignedCities, selectedPlaceType, blockedCountryIds, blockedStateIds, blockedDivisionIds, selectedOffices, isCurrentOfficeSelected]);

    // useEffect(() => {
    //     if (!map || !searchBoxRef.current) return;

    //     const searchBox = new google.maps.places.SearchBox(searchBoxRef.current);

    //     const placesChangedListener = searchBox.addListener('places_changed', () => {
    //         const places = searchBox.getPlaces();
    //         if (!places || places.length === 0) return;

    //         const place = places[0];
    //         console.log('place selected from search box', place);
    //         if (!place.geometry || !place.geometry.location) return;

    //         // Fetch administrative area details
    //         const geocoder = new google.maps.Geocoder();
    //         geocoder.geocode({ location: place.geometry.location }, (results, geoStatus) => {
    //             if (geoStatus === google.maps.GeocoderStatus.OK && results) {
    //                 const adminArea = results.find(result =>
    //                     result.types.includes('administrative_area_level_1')
    //                 );

    //                 const divisionArea = results.find(result =>
    //                     result.types.includes('administrative_area_level_2')
    //                 );

    //                 const country = results.find(result =>
    //                     result.types.includes('country')
    //                 );
    //                 // Find the locality/city from reverse geocode results
    //                 const locality = results.find(result =>
    //                     result.types.includes('locality')
    //                 );

    //                 const placeId = place.place_id as string;

    //                 let countryPlaceId: string | null = null;
    //                 let statePlaceId: string | null = null;
    //                 let divisionPlaceId: string | null = null;
    //                 let cityPlaceId: string | null = null;

    //                 const isCountry = place.types?.includes('country');
    //                 const isState = place.types?.includes('administrative_area_level_1');
    //                 const isDivision = place.types?.includes('administrative_area_level_2');

    //                 if (isCountry) {
    //                     countryPlaceId = placeId;
    //                 } else if (isState) {
    //                     statePlaceId = placeId;
    //                     if (country) {
    //                         countryPlaceId = (country as any).place_id;
    //                     }
    //                 } else if (isDivision) {
    //                     divisionPlaceId = placeId;
    //                     if (country) {
    //                         countryPlaceId = (country as any).place_id;
    //                     }
    //                     if (adminArea) {
    //                         statePlaceId = (adminArea as any).place_id;
    //                     }
    //                 }
    //                 else {
    //                     if (country) {
    //                         countryPlaceId = (country as any).place_id;
    //                     }
    //                     if (adminArea) {
    //                         statePlaceId = (adminArea as any).place_id;
    //                     }
    //                     if (divisionArea) {
    //                         divisionPlaceId = (divisionArea as any).place_id;
    //                     }
    //                     if (locality) {
    //                         cityPlaceId = (locality as any).place_id;
    //                     }
    //                 }

    //                 // 1. Check if city is already assigned
    //                 const existing = localAssignments.find(a => a.city_providerId === placeId);
    //                 if (existing && existing.officeId !== currentOffice.id) {
    //                     toast.warning('This city is already assigned to another office', { ...toastOptObj });
    //                     return;
    //                 }

    //                 if (existing && existing.officeId === currentOffice.id) {
    //                     toast.warning('This city is already assigned to the current office', { ...toastOptObj });
    //                     return;
    //                 }

    //                 // 2. Check if division is already assigned
    //                 const existingDivision = localAssignments.find(a => a.division_providerId === divisionPlaceId && !a.city_providerId && !a.cityId);
    //                 if (existingDivision && existingDivision.officeId !== currentOffice.id) {
    //                     toast.warning('This division or its cities are already assigned to another office', { ...toastOptObj });
    //                     return;
    //                 }
    //                 if (existingDivision && existingDivision.officeId === currentOffice.id) {
    //                     toast.warning('This division or its cities are already assigned to the current office', { ...toastOptObj });
    //                     return;
    //                 }

    //                 // 3. Check if parent state is already assigned
    //                 const assignState = localAssignments.find(a => a.state_providerId === statePlaceId && !a.city_providerId && !a.cityId);
    //                 if (assignState && assignState.officeId !== currentOffice.id) {
    //                     toast.warning('This state or cities/divisions of this state is already assigned to another office', { ...toastOptObj });
    //                     return;
    //                 }

    //                 if (assignState && assignState.officeId === currentOffice.id) {
    //                     toast.warning('This state or cities/divisions of this state is already assigned to the current office', { ...toastOptObj });
    //                     return;
    //                 }

    //                 // 4. Check if parent country is already assigned
    //                 const assignCountry = localAssignments.find(a => a.country_providerId === countryPlaceId && !a.state_providerId && !a.city_providerId);
    //                 if (assignCountry && assignCountry.officeId !== currentOffice.id) {
    //                     toast.warning('This country or its states/divisions/cities are already assigned to another office', { ...toastOptObj });
    //                     return;
    //                 }

    //                 if (assignCountry && assignCountry.officeId === currentOffice.id) {
    //                     toast.warning('This country or its states/divisions/cities are already assigned to the current office', { ...toastOptObj });
    //                     return;
    //                 }

    //                 // Check for hierarchical blocks
    //                 if (
    //                     place.types?.includes('country') &&
    //                     placeId &&
    //                     blockedCountryIds.has(placeId)
    //                 ) {
    //                     toast.warning(
    //                         'Please remove mapped states/divisions/cities before assigning a country.',
    //                         { ...toastOptObj }
    //                     );
    //                     return;
    //                 }

    //                 if (
    //                     place.types?.includes('administrative_area_level_1') &&
    //                     placeId &&
    //                     blockedStateIds.has(placeId)
    //                 ) {
    //                     toast.warning(
    //                         'Please remove mapped cities/divisions before assigning a state.',
    //                         { ...toastOptObj }
    //                     );
    //                     return;
    //                 }

    //                 if (
    //                     place.types?.includes('administrative_area_level_2') &&
    //                     placeId &&
    //                     blockedDivisionIds.has(placeId)
    //                 ) {
    //                     toast.warning(
    //                         'Please remove mapped cities before assigning a division.',
    //                         { ...toastOptObj }
    //                     );
    //                     return;
    //                 }

    //                 // Apply boundaries by adding placeId to selected cities
    //                 if (place.place_id) {
    //                     setSelectedUnassignedCities(prev => {
    //                         let updated = [...prev];

    //                         if (!updated.includes(place.place_id!)) {
    //                             updated.push(place.place_id!);
    //                         }

    //                         // Only add cityPlaceId if the search result is actually for a locality/city
    //                         const isCountry = place.types?.includes('country');
    //                         const isState = place.types?.includes('administrative_area_level_1');

    //                         if (cityPlaceId && !isCountry && !isState && !updated.includes(cityPlaceId)) {
    //                             updated.push(cityPlaceId);
    //                         }

    //                         return updated;
    //                     });

    //                     setSelectedAreas(prev => {
    //                         const isCountry = place.types?.includes('country');
    //                         const isState = place.types?.includes('administrative_area_level_1');
    //                         const isDivision = place.types?.includes('administrative_area_level_2');

    //                         const newPlaceDetails: RegionDetailsEntity = {
    //                             officeId: currentOffice.id,
    //                             type: isCountry
    //                                 ? 'COUNTRY'
    //                                 : isState
    //                                     ? 'ADMINISTRATIVE_AREA_LEVEL_1'
    //                                     : isDivision
    //                                         ? 'ADMINISTRATIVE_AREA_LEVEL_2'
    //                                         : 'LOCALITY',
    //                             country_providerId: countryPlaceId ?? undefined,
    //                             division_providerId: isCountry || isState ? undefined : (divisionPlaceId ?? undefined),
    //                             state_providerId: isCountry ? undefined : (statePlaceId ?? undefined),
    //                             city_providerId: (isCountry || isState) ? undefined : (cityPlaceId ?? undefined),
    //                             placeName: place.formatted_address || '',
    //                         }

    //                         if (isCountry && prev.some((a: any) => a?.country_providerId === countryPlaceId && a?.type === 'COUNTRY')) {
    //                             return prev;
    //                         }
    //                         if (isState && prev.some((a: any) => a?.state_providerId === statePlaceId && a?.type === 'ADMINISTRATIVE_AREA_LEVEL_1')) {
    //                             return prev;
    //                         }
    //                         if (isDivision && prev.some((a: any) => a?.division_providerId === divisionPlaceId && a?.type === 'ADMINISTRATIVE_AREA_LEVEL_2')) {
    //                             return prev;
    //                         }
    //                         if (!isCountry && !isState && prev.some((a: any) => a?.city_providerId === cityPlaceId)) {
    //                             return prev;
    //                         }

    //                         return [...prev, newPlaceDetails];
    //                     })

    //                     setSearchValue('');

    //                 }
    //             }
    //         });
    //     });

    //     const boundsChangedListener = map.addListener('bounds_changed', () => {
    //         const bounds = map.getBounds();
    //         if (bounds) {
    //             searchBox.setBounds(bounds);
    //         }
    //     });

    //     const fullscreenListener = map.addListener('fullscreen_changed', () => {
    //         const bounds = map.getBounds();
    //         if (bounds) {
    //             searchBox.setBounds(bounds);
    //         }
    //     });


    //     return () => {
    //         google.maps.event.removeListener(placesChangedListener);
    //         google.maps.event.removeListener(boundsChangedListener);
    //         google.maps.event.removeListener(fullscreenListener);
    //     };
    // }, [map, localAssignments, currentOffice, selectedAreas, blockedCountryIds, blockedStateIds]);

    const getDefaultStrokeByType = (type: PlaceSelectType) => {
        switch (type) {
            case 'COUNTRY':
                return { color: '#1e42e5ff', weight: 2, opacity: 0.6 };
            case 'ADMINISTRATIVE_AREA_LEVEL_1':
                return { color: '#1e42e5ff', weight: 1.5, opacity: 0.6 };
            case 'ADMINISTRATIVE_AREA_LEVEL_2':
                return { color: '#1e42e5ff', weight: 1, opacity: 0.6 };
            case 'LOCALITY':
                return { color: '#5772ea', weight: 0.8, opacity: 0.6 };
            default:
                return null;
        }
    };

    const fetchSuggestions = (query: string) => {
        if (!query || !window.google) return;

        const service = new google.maps.places.AutocompleteService();

        service.getPlacePredictions(
            {
                input: query,
                types:
                    selectedPlaceType === 'COUNTRY'
                        ? ['country']
                        : selectedPlaceType === 'ADMINISTRATIVE_AREA_LEVEL_1'
                            ? ['administrative_area_level_1']
                            : selectedPlaceType === 'ADMINISTRATIVE_AREA_LEVEL_2'
                                ? ['administrative_area_level_2']
                                : ['locality']
            },
            (predictions, status) => {
                if (
                    status === google.maps.places.PlacesServiceStatus.OK &&
                    predictions
                ) {
                    setSuggestions(predictions);
                } else {
                    setSuggestions([]);
                }
            }
        );
    };

    const isSameAreaByType = (
        area: RegionDetailsEntity,
        placeId: string,
        type: PlaceSelectType
    ) => {
        switch (type) {
            case 'COUNTRY':
                return area.country_providerId === placeId;
            case 'ADMINISTRATIVE_AREA_LEVEL_1':
                return area.state_providerId === placeId;
            case 'ADMINISTRATIVE_AREA_LEVEL_2':
                return area.division_providerId === placeId;
            case 'LOCALITY':
            default:
                return area.city_providerId === placeId;
        }
    };

    const hansleSelectSuggestion = (suggestion: any) => {
        if (!suggestion || !suggestion.place_id || !map) return;

        const placeId = suggestion.place_id;

        const service = new google.maps.places.PlacesService(map);

        service.getDetails(
            { placeId: placeId, fields: ['geometry', 'name', 'type', 'formatted_address', 'place_id'] },
            (place, status) => {
                if (status === google.maps.places.PlacesServiceStatus.OK && place?.geometry?.location) {
                    const geocoder = new google.maps.Geocoder();

                    geocoder.geocode({ location: place.geometry.location }, (results, geoStatus) => {
                        if (geoStatus === google.maps.GeocoderStatus.OK && results) {
                            console.log('place selected from autocomplete', results);
                            const adminArea = results.find(result =>
                                result.types.includes('administrative_area_level_1')
                            );

                            const divisionArea = results.find(result =>
                                result.types.includes('administrative_area_level_2')
                            );

                            const country = results.find(result =>
                                result.types.includes('country')
                            );
                            // Find the locality/city from reverse geocode results
                            const locality = results.find(result =>
                                result.types.includes('locality')
                            );

                            console.log({ adminArea, divisionArea, country, locality });

                            let countryPlaceId: string | null = null;
                            let statePlaceId: string | null = null;
                            let divisionPlaceId: string | null = null;
                            let cityPlaceId: string | null = null;

                            const isCountry = place.types?.includes('country');
                            const isState = place.types?.includes('administrative_area_level_1');
                            const isDivision = place.types?.includes('administrative_area_level_2');

                            if (isCountry) {
                                countryPlaceId = placeId;
                            } else if (isState) {
                                statePlaceId = placeId;
                                if (country) {
                                    countryPlaceId = (country as any).place_id;
                                }
                            } else if (isDivision) {
                                divisionPlaceId = placeId;
                                if (country) {
                                    countryPlaceId = (country as any).place_id;
                                }
                                if (adminArea) {
                                    statePlaceId = (adminArea as any).place_id;
                                }
                            }
                            else {
                                if (country) {
                                    countryPlaceId = (country as any).place_id;
                                }
                                if (adminArea) {
                                    statePlaceId = (adminArea as any).place_id;
                                }
                                if (divisionArea) {
                                    divisionPlaceId = (divisionArea as any).place_id;
                                }
                                if (locality) {
                                    cityPlaceId = (locality as any).place_id
                                }
                                if (selectedPlaceType === "LOCALITY" && (!cityPlaceId || IsStringNullEmptyOrUndefined(cityPlaceId))) {
                                    cityPlaceId = place && place.place_id && !IsStringNullEmptyOrUndefined(place.place_id) ? place.place_id : null
                                }
                            }

                            console.log({ countryPlaceId, statePlaceId, divisionPlaceId, cityPlaceId });

                            const isInvalidHierarchy =
                                (selectedPlaceType === 'LOCALITY' && (!placeId || !statePlaceId || !countryPlaceId)) ||

                                (selectedPlaceType === 'ADMINISTRATIVE_AREA_LEVEL_2' &&
                                    (!divisionPlaceId || !statePlaceId || !countryPlaceId ||
                                        divisionPlaceId === statePlaceId)) ||

                                (selectedPlaceType === 'ADMINISTRATIVE_AREA_LEVEL_1' &&
                                    (!statePlaceId || !countryPlaceId ||
                                        statePlaceId === countryPlaceId)) ||

                                (selectedPlaceType === 'COUNTRY' && !countryPlaceId);

                            if (isInvalidHierarchy) {
                                toast.warning('Mapping skipped due to incomplete or restricted location data', {
                                    ...toastOptObj
                                });
                                return;
                            }

                            // 1. Check if city is already assigned
                            const existing = localAssignments.find(a => a.city_providerId === placeId);
                            if (existing && existing.officeId !== currentOffice.id) {
                                toast.warning('This city is already assigned to another office', { ...toastOptObj });
                                return;
                            }

                            if (existing && existing.officeId === currentOffice.id) {
                                toast.warning('This city is already assigned to the current office', { ...toastOptObj });
                                return;
                            }

                            // 2. Check if division is already assigned
                            const existingDivision = localAssignments.find(a => a.division_providerId === divisionPlaceId && !a.city_providerId && !a.cityId);
                            if (existingDivision && existingDivision.officeId !== currentOffice.id) {
                                toast.warning('This division or its cities are already assigned to another office', { ...toastOptObj });
                                return;
                            }
                            if (existingDivision && existingDivision.officeId === currentOffice.id) {
                                toast.warning('This division or its cities are already assigned to the current office', { ...toastOptObj });
                                return;
                            }

                            // 3. Check if parent state is already assigned
                            const assignState = localAssignments.find(a => a.state_providerId === statePlaceId && !a.city_providerId && !a.cityId && !a.division_providerId);
                            if (assignState && assignState.officeId !== currentOffice.id) {
                                toast.warning('This state or cities/divisions of this state is already assigned to another office', { ...toastOptObj });
                                return;
                            }

                            if (assignState && assignState.officeId === currentOffice.id) {
                                toast.warning('This state or cities/divisions of this state is already assigned to the current office', { ...toastOptObj });
                                return;
                            }

                            // 4. Check if parent country is already assigned
                            const assignCountry = localAssignments.find(a => a.country_providerId === countryPlaceId && !a.state_providerId && !a.city_providerId);
                            if (assignCountry && assignCountry.officeId !== currentOffice.id) {
                                toast.warning('This country or its states/divisions/cities are already assigned to another office', { ...toastOptObj });
                                return;
                            }

                            if (assignCountry && assignCountry.officeId === currentOffice.id) {
                                toast.warning('This country or its states/divisions/cities are already assigned to the current office', { ...toastOptObj });
                                return;
                            }

                            // Check for hierarchical blocks
                            if (
                                place.types?.includes('country') &&
                                placeId &&
                                blockedCountryIds.has(placeId)
                            ) {
                                toast.warning(
                                    'Please remove mapped states/divisions/cities before assigning a country.',
                                    { ...toastOptObj }
                                );
                                return;
                            }

                            if (
                                place.types?.includes('administrative_area_level_1') &&
                                placeId &&
                                blockedStateIds.has(placeId)
                            ) {
                                toast.warning(
                                    'Please remove mapped cities/divisions before assigning a state.',
                                    { ...toastOptObj }
                                );
                                return;
                            }

                            if (
                                place.types?.includes('administrative_area_level_2') &&
                                placeId &&
                                blockedDivisionIds.has(placeId)
                            ) {
                                toast.warning(
                                    'Please remove mapped cities before assigning a division.',
                                    { ...toastOptObj }
                                );
                                return;
                            }

                            // Apply boundaries by adding placeId to selected cities
                            if (placeId) {
                                setSelectedUnassignedCities(prev => {
                                    let updated = [...prev];

                                    if (!updated.includes(place.place_id!)) {
                                        updated.push(place.place_id!);
                                    }

                                    // Only add cityPlaceId if the search result is actually for a locality/city
                                    const isCountry = place.types?.includes('country');
                                    const isState = place.types?.includes('administrative_area_level_1');

                                    if (cityPlaceId && !isCountry && !isState && !updated.includes(cityPlaceId)) {
                                        updated.push(cityPlaceId);
                                    }

                                    return updated;
                                });

                                setSelectedAreas(prev => {
                                    const isCountry = place.types?.includes('country');
                                    const isState = place.types?.includes('administrative_area_level_1');
                                    const isDivision = place.types?.includes('administrative_area_level_2');

                                    const newPlaceDetails: RegionDetailsEntity = {
                                        officeId: currentOffice.id,
                                        type: isCountry
                                            ? 'COUNTRY'
                                            : isState
                                                ? 'ADMINISTRATIVE_AREA_LEVEL_1'
                                                : isDivision
                                                    ? 'ADMINISTRATIVE_AREA_LEVEL_2'
                                                    : 'LOCALITY',
                                        country_providerId: countryPlaceId ?? undefined,
                                        division_providerId: isCountry || isState ? undefined : (divisionPlaceId ?? undefined),
                                        state_providerId: isCountry ? undefined : (statePlaceId ?? undefined),
                                        city_providerId: (isCountry || isState) ? undefined : (cityPlaceId ?? undefined),
                                        placeName: (!isCountry && !isState && !isDivision) && place && place.name
                                            ? (() => {
                                                const getAddressComponent = (type: string) => {
                                                    for (const res of results || []) {
                                                        const comp = res.address_components ? res.address_components.find((c: any) => c.types && c.types.includes(type)) : null;
                                                        if (comp) return comp.long_name;
                                                    }
                                                    return '';
                                                };
                                                const cityName = place.name;
                                                const districtName = getAddressComponent('administrative_area_level_3') || getAddressComponent('administrative_area_level_2');
                                                const stateName = getAddressComponent('administrative_area_level_1');
                                                const countryName = getAddressComponent('country');
                                                return districtName
                                                    ? `${cityName}, ${districtName}, ${stateName}, ${countryName}`
                                                    : `${cityName}, ${stateName}, ${countryName}`;
                                            })()
                                            : (place ? place.formatted_address : ''),
                                    }

                                    if (isCountry && prev.some((a: any) => a?.country_providerId === countryPlaceId && a?.type === 'COUNTRY')) {
                                        return prev;
                                    }
                                    if (isState && prev.some((a: any) => a?.state_providerId === statePlaceId && a?.type === 'ADMINISTRATIVE_AREA_LEVEL_1')) {
                                        return prev;
                                    }
                                    if (isDivision && prev.some((a: any) => a?.division_providerId === divisionPlaceId && a?.type === 'ADMINISTRATIVE_AREA_LEVEL_2')) {
                                        return prev;
                                    }
                                    if (!isCountry && !isState && prev.some((a: any) => a?.city_providerId === cityPlaceId)) {
                                        return prev;
                                    }

                                    return [...prev, newPlaceDetails];
                                })

                                setSearchValue('');

                            }
                        }
                    });
                }
            }
        );
    };


    return (
        <>
            <div ref={searchControlRef}>
                <div style={{
                    top: '10px',
                    right: '60px',
                    zIndex: 999999,
                    margin: '10px',
                    padding: '10px',
                    display: 'flex',
                    gap: '10px',
                    alignItems: 'center',
                    flexDirection: 'column',
                    backgroundColor: 'white',
                    borderRadius: '4px',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                    width: '250px'
                }}>
                    <Dropdown
                        value={selectedPlaceType}
                        options={placeTypeOptions}
                        onChange={(e) => setSelectedPlaceType(e.value)}
                        placeholder="Select Place Type"
                        className="w-100"
                        style={{ minWidth: '200px' }}
                        appendTo="self"
                    />
                    <div style={{ position: "relative", width: '100%' }}>
                        <AutoComplete
                            value={searchValue}
                            suggestions={suggestions}
                            completeMethod={(e) => fetchSuggestions(e.query)}
                            field="description"
                            onChange={(e) => setSearchValue(e.value)}
                            onSelect={(e) => hansleSelectSuggestion(e.value)}
                            placeholder="Search by place name"
                            className="w-full w-100"
                            inputStyle={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', paddingRight: '30px' }}
                            panelStyle={{ left: "51%", maxWidth: "150px" }}
                            appendTo="self"
                            inputClassName="w-full pe-5"
                        />
                        {searchValue && (
                            <i
                                className="pi pi-times"
                                style={{
                                    position: "absolute",
                                    right: "10px",
                                    top: "50%",
                                    transform: "translateY(-50%)",
                                    cursor: "pointer",
                                    zIndex: 500
                                }}
                                onClick={() => {
                                    setSearchValue('');
                                    if (searchBoxRef.current) {
                                        searchBoxRef.current.value = '';
                                    }
                                }}
                            />
                        )}
                    </div>
                </div>
            </div>

            <div ref={legendControlRef}>
                <div className="card shadow-sm" style={{
                    bottom: '10px',
                    left: '10px',
                    zIndex: 10,
                    minWidth: '250px',
                    borderRadius: '4px'
                }}>
                    <div className="card-body p-3">
                        <h4 className="font-weight-bold mb-3">
                            Office Assignment Colors
                        </h4>
                        <div
                            className="d-flex align-items-center mb-2"
                            onClick={() => setIsCurrentOfficeSelected(prev => !prev)}
                            style={{
                                cursor: "pointer",
                                background: !isCurrentOfficeSelected ? "#eef2ff" : "transparent",
                                opacity: isCurrentOfficeSelected ? 1 : 0.4,
                                padding: "4px 6px",
                                borderRadius: "4px",
                                transition: "all 0.2s"
                            }}
                        >
                            <div
                                style={{
                                    width: '24px',
                                    height: '18px',
                                    backgroundColor: '#E65100',
                                    border: '2px solid #BF360C',
                                    borderRadius: '3px',
                                    marginRight: '10px',
                                }}
                            />
                            <small
                                style={{
                                    fontWeight: 600,
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis'
                                }}
                                title={currentOffice.name ?? currentOffice.id}
                            >
                                {currentOffice.name ?? 'Current Office'} (Current Office)
                            </small>
                        </div>
                        <hr />
                        <div
                            style={{
                                maxHeight: '160px',
                                overflowY: 'auto',
                                paddingRight: '4px'
                            }}
                        >
                            {officesForLegend.filter(office => office.officeId !== currentOffice.id).map(({ officeId, officeName }) => {
                                const color = generateOfficeColor(officeId);
                                const isSelected = selectedOffices.includes(officeId);

                                return (
                                    <div
                                        key={officeId}
                                        onClick={() => toggleOffice(officeId)}
                                        className="d-flex align-items-center mb-2"
                                        style={{
                                            cursor: "pointer",
                                            background: !isSelected ? "#eef2ff" : "transparent",
                                            opacity: isSelected ? 1 : 0.4,
                                            padding: "4px 6px",
                                            borderRadius: "4px",
                                            transition: "all 0.2s"
                                        }}
                                    >
                                        <div
                                            style={{
                                                width: '24px',
                                                height: '18px',
                                                backgroundColor: color.fill,
                                                border: `2px solid ${color.stroke}`,
                                                borderRadius: '3px',
                                                marginRight: '10px'
                                            }}
                                        />
                                        <small
                                            style={{
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis'
                                            }}
                                            title={officeId}
                                        >
                                            {officeName}
                                        </small>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default MapFeatureLayer